import * as Yup from "yup";
import {
  ALPHA_NUMERIC_REGEX,
  EMAIL_REGEX,
  WHITE_SPACES_REGEX,
  userRole,
} from "../constants";
import { getLocalizedMessage } from "../UtilityFunctions/utils";

export const addUserFormFieldNames = {
  NAME: "name",
  EMAIL: "email",
  MOB_NUM: "mobileNumber",
  COUNTRY_ID: "countryId",
  EMP_ID: "employeeId",
  ADDRESS: "address",
  GLOBAL_ID: "globalId",
  ROLES_ID: "rolesId",
  COUNTRY_CODE: "countryCode",
};

export const addUserFormInitialValues = {
  [addUserFormFieldNames.NAME]: "",
  [addUserFormFieldNames.EMAIL]: "",
  [addUserFormFieldNames.EMP_ID]: "",
  [addUserFormFieldNames.ADDRESS]: "",
  [addUserFormFieldNames.MOB_NUM]: "",
  [addUserFormFieldNames.ROLES_ID]: [],
  [addUserFormFieldNames.COUNTRY_ID]: "",
  [addUserFormFieldNames.GLOBAL_ID]: "",
  [addUserFormFieldNames.COUNTRY_CODE]: "",
};

const validateEmployeeId = (roles = []) => {
  return (
    roles?.includes(userRole.ADMIN) ||
    roles?.includes(userRole.TM) ||
    roles?.includes(userRole.CA)
  );
};

const validateCountryId = (roles = []) => {
  return (
    roles?.includes(userRole.DNSD) ||
    roles?.includes(userRole.TM) ||
    roles?.includes(userRole.CA)
  );
};

const validateGlobalId = (roles = []) => {
  return roles?.includes?.(userRole.DNSD);
};

export const validationSchema = (intl, validateObj) => {
  return Yup.object().shape({
    [addUserFormFieldNames.NAME]: Yup.string()
      .required(intl.formatMessage({ id: "addUser.fieldError.name.required" }))
      .max(200),
    [addUserFormFieldNames.EMAIL]: Yup.string()
      .email(intl.formatMessage({ id: "addUser.fieldError.email.invalid" }))
      .required(intl.formatMessage({ id: "addUser.fieldError.email.required" }))
      .matches(
        EMAIL_REGEX,
        getLocalizedMessage(intl, "addUser.fieldError.email.invalid")
      )
      .matches(
        WHITE_SPACES_REGEX,
        getLocalizedMessage(intl, "addUser.fieldError.email.invalid")
      ),

    [addUserFormFieldNames.MOB_NUM]: Yup.string()
    .min(6, getLocalizedMessage(intl, 'addUser.fieldError.mobile.min')) 
    .max(15, getLocalizedMessage(intl, 'addUser.fieldError.mobile.max'))
    .test(
      'require-country-code',
      getLocalizedMessage(intl, 'addUser.fieldError.mobile.invalid'),
      function (value) {
        const code = this.parent?.[addUserFormFieldNames.COUNTRY_CODE]; // Access sibling field
        if (!value && code) {
          // If MOB_NUM is present but COUNTRY_CODE is not
          return false; // Validation fails
        }
        return true; // Validation passes
      }
    )
    // ?.test(
    //   "mobileNumber",
    //   intl.formatMessage({ id: "addUser.fieldError.mobile.invalid" }),
    //   function (value) {
    //     const { countryCode } = this.parent;
    //     const selectedCountryCode = countryCode?.replace("+", "");

    //     const validationRules = validateObj;

    //     if (!value && !countryCode) {
    //       return true; // No error if both countryCode and mobileNumber are empty
    //     }
    //     if (!countryCode && value) {
    //       return false; // Validation error if either countryCode or mobileNumber is missing
    //     }
    //     if (!value && countryCode) {
    //       return true; // No error if code is there but no mobile number
    //     }

    //     const regexPattern = validationRules[selectedCountryCode];
    //     return regexPattern?.test(value);
    //   }
    // )
    ,
    [addUserFormFieldNames.COUNTRY_CODE]: Yup.lazy((_, context) => {
      const mobNum = context?.parent?.[addUserFormFieldNames.MOB_NUM];
      return mobNum
        ? Yup.string().required(getLocalizedMessage(intl, 'addUser.fieldError.countryCode.invalid'))
        : Yup.string().nullable();
    }),

    [addUserFormFieldNames.ROLES_ID]: Yup.array()
      .of(
        Yup.number().required(
          intl.formatMessage({ id: "addUser.fieldError.userRole.required" })
        )
      )
      .min(
        1,
        intl.formatMessage({
          id: "addUser.fieldError.userRole.select.atleast1",
        })
      ),

    [addUserFormFieldNames.ADDRESS]: Yup.string().when(
      addUserFormFieldNames.ROLES_ID,
      {
        is: validateGlobalId,
        then: () =>
          Yup.string()
            .required(
              intl.formatMessage({
                id: "addUser.fieldError.address.required",
              })
            )
            .max(400),
        otherwise: () => Yup.string().nullable(),
      }
    ),

    [addUserFormFieldNames.EMP_ID]: Yup.string().when(
      addUserFormFieldNames.ROLES_ID,
      {
        is: validateEmployeeId,
        then: () =>
          Yup.string()
            .required(
              intl.formatMessage({
                id: "addUser.fieldError.employeeId.required",
              })
            )
            .max(15)
            .matches(
              WHITE_SPACES_REGEX,
              intl.formatMessage({
                id: "addUser.fieldError.employeeId.invalid",
              })
            )
            .matches(
              ALPHA_NUMERIC_REGEX,
              intl.formatMessage({
                id: "addUser.fieldError.employeeId.invalid",
              })
            ),
        otherwise: () => Yup.string().nullable(),
      }
    ),

    [addUserFormFieldNames.COUNTRY_ID]: Yup.number().when(
      addUserFormFieldNames.ROLES_ID,
      {
        is: validateCountryId,
        then: () =>
          Yup.string().required(
            intl.formatMessage({ id: "addUser.fieldError.countryId.required" })
          ),
        otherwise: () => Yup.string().nullable(),
      }
    ),

    [addUserFormFieldNames.GLOBAL_ID]: Yup.string().when(
      addUserFormFieldNames.ROLES_ID,
      {
        is: validateGlobalId,
        then: () =>
          Yup.string()
            .required(
              intl.formatMessage({ id: "addUser.fieldError.globalId.required" })
            )
            .max(15)
            .matches(
              WHITE_SPACES_REGEX,
              intl.formatMessage({ id: "addUser.fieldError.globalId.invalid" })
            )
            .matches(
              ALPHA_NUMERIC_REGEX,
              intl.formatMessage({ id: "addUser.fieldError.globalId.invalid" })
            ),
        otherwise: () => Yup.string().nullable(),
      }
    ),
  });
};
